import { Autocomplete, autocompleteClasses, inputBaseClasses, TextField, Tooltip } from '@mui/material';
import { MultiTextField } from 'interfaces/genericFields';
import { compact, isEmpty, last, map } from 'es-toolkit/compat';
import React, { SyntheticEvent } from 'react';
import { EnumDisplayNames } from 'utils/queryHooks/uiConstantsHooks';
import { HeaderCellEditorProps } from '.';
import { useTableEditingContext } from '../TableEditingContext';
import { BasicTableRow } from '../TableEditingContext/types';

const HeaderMultiTextInput = <R extends BasicTableRow, V, Context extends {} = {}>({
  onChange,
  value,
  field,
}: HeaderCellEditorProps<R, V, Context, MultiTextField>) => {
  const handleChange = (event: SyntheticEvent<Element, Event>, nextValue: string | string[]) => {
    if (last(nextValue) === '') {
      onChange([]);
      return;
    }
    const effectiveSelection = compact(nextValue);
    onChange(effectiveSelection);
  };

  const { fieldsContext } = useTableEditingContext<R, Context>();

  const { enumType, options: manualOptions, optionsGetter } = field;

  const options: (string | number)[] = React.useMemo(() => {
    const baseOptions = enumType
      ? (fieldsContext as { enumDisplayNames?: EnumDisplayNames })?.enumDisplayNames?.[enumType] || []
      : optionsGetter
      ? optionsGetter(fieldsContext)
      : manualOptions;
    return map(baseOptions, 'value');
  }, [enumType, fieldsContext, optionsGetter, manualOptions]);

  return (
    <Tooltip title="Press Enter to add a new value" placement="top">
      <Autocomplete<string | number, boolean, boolean, boolean>
        multiple
        size="small"
        value={isEmpty(value) ? [] : value || ''}
        onChange={handleChange}
        onBlur={(e) => {
          // https://github.com/mui/mui-x/issues/3393 - MUI Grid has a bug with components that use portals in header cells
          e.stopPropagation();
        }}
        freeSolo={true}
        open={isEmpty(options) ? false : undefined}
        options={options}
        renderInput={(params) => <TextField {...params} fullWidth label={field.label} size="small" />}
        sx={{
          pt: 1,
          width: '100%',
          [`& .${autocompleteClasses.inputRoot}.${inputBaseClasses.root} input`]: { width: '100%' },
        }}
      />
    </Tooltip>
  );
};

export default HeaderMultiTextInput;
